import React from 'react';
import PropTypes from 'prop-types';

import BubbleContent from '../content/bubble_content';
import Communicator from 'models/communicator';
import ConversationItem from 'models/conversation_item';
import ConversationMessageAttachments from 'components/customer/conversation_history/conversation_items_v2/message/attachments/conversation_message_attachments';
import InlineAnnotatedContentContainer from 'components/lib/inline_annotated_content_container';
import ItemCommError from '../item_comm_error';
import ItemContent from '../item_content';
import ItemMetadata from '../item_metadata';
import { MessageChannelType, MessageStatus } from 'models/conversation_message.js';
import TwitterMetadataIcon from './twitter_metadata_icon';
import WhatsAppMetadataIcon from './whatsapp_metadata_icon';
import InstagramMetadataIcon from './instagram_metadata_icon';
import MessageReaction from './message_reaction';

export default function MessageItem({
  friendlyCompanyAddress,
  friendlyCustomerAddress,
  hasReaction,
  isFirstInStack,
  isLastInStack,
  isStacked,
  item,
}) {
  let channelType = item.content.getChannelType();
  return (
    <ItemContent
      isFirstInStack={isFirstInStack}
      isLastInStack={isLastInStack}
      isStacked={isStacked}
      item={item}
      showIcon={!isStacked}
    >
      <ItemMetadata
        icon={getIcon(channelType)}
        item={item}
        showMetadata={!isStacked}
        status={getStatus(item)}
        text={getMetadataText(item)}
        tooltip={getTooltipText(item)}
      />
      <MessageContent item={item} hasReaction={hasReaction} />
      <ItemCommError item={item} />
    </ItemContent>
  );

  function getIcon(channel) {
    // Add future messaging channel icons here
    switch (channel) {
      case MessageChannelType.TWITTER:
        return <TwitterMetadataIcon />;
      case MessageChannelType.WHATSAPP:
        return <WhatsAppMetadataIcon />;
      case MessageChannelType.INSTAGRAM_DIRECT:
        return <InstagramMetadataIcon />;
      default:
        return null;
    }
  }

  function getStatus(item) {
    let status = item.content.getStatus();
    switch (status) {
      case MessageStatus.DELIVERED:
      case MessageStatus.SENT:
        return 'delivered';
      case MessageStatus.FAILED:
      case MessageStatus.UNDELIVERED:
      case MessageStatus.UNKNOWN:
        return 'delivery failed';
      case MessageStatus.NEW:
      case MessageStatus.SENDING:
        return 'sending';
      case MessageStatus.RECEIVED:
        return 'received';
      default:
        return undefined;
    }
  }

  function getMetadataText(item) {
    switch (item.initiator.type) {
      case Communicator.AGENT:
        return `replied via ${item.content.getChannelName()}`;
      case Communicator.CUSTOMER:
        return `sent a ${item.content.getChannelName()}`;
    }
    return null;
  }

  function getTooltipText() {
    if (!friendlyCustomerAddress || !friendlyCompanyAddress) {
      return null;
    }

    switch (item.initiator.type) {
      case Communicator.AGENT:
        return `sent from ${friendlyCompanyAddress} to ${friendlyCustomerAddress}`;
      case Communicator.CUSTOMER:
        return `sent from ${friendlyCustomerAddress} to ${friendlyCompanyAddress}`;
    }
    return null;
  }
}

MessageItem.propTypes = {
  friendlyCompanyAddress: PropTypes.string,
  friendlyCustomerAddress: PropTypes.string,
  hasReaction: PropTypes.bool,
  isFirstInStack: PropTypes.bool,
  isLastInStack: PropTypes.bool,
  isStacked: PropTypes.bool,
  item: PropTypes.instanceOf(ConversationItem).isRequired,
};

export function MessageContent({ item, hasReaction }) {
  let isAutoreply = item.initiator.type === Communicator.AUTOMATED;
  return (
    <BubbleContent className="messageItem-content" item={item} hasReaction={hasReaction}>
      {isAutoreply ? <div className="messageItem-autoreply">Autoreply</div> : null}
      {renderText()}
      {renderAttachments()}
      {renderReaction()}
    </BubbleContent>
  );

  function renderText() {
    return (
      <InlineAnnotatedContentContainer
        hasRedactedPaymentCard={item.content.hasRedactedPaymentCardNumber()}
        id={item.id}
        text={item.content.getBody()}
      />
    );
  }

  function renderAttachments() {
    let itemAttachments = item.content.getAttachments();
    if (itemAttachments.length === 0) {
      return null;
    }
    return <ConversationMessageAttachments attachments={itemAttachments} />;
  }

  function renderReaction() {
    if (item.content.reaction && item.content.reaction.hasReaction()) {
      return <MessageReaction timestamp={item.content.reaction.timestamp} />;
    }
    return null;
  }
}

MessageContent.propTypes = {
  item: PropTypes.instanceOf(ConversationItem).isRequired,
  hasReaction: PropTypes.bool,
};



// WEBPACK FOOTER //
// ./src/scripts/presentation/components/customer/conversation_history/conversation_items_v2/message/message_item.jsx